import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, PaymentService } from 'lib-trend-core';
import { takeUntil } from 'rxjs';
import { environment } from '../../../../environments/environment';

export type BillingCycle = 'yearly' | 'semiannual' | 'monthly';

export interface PlanLimits {
  campaigns: string;
  integrations: string;
  contacts: string;
}

export interface Plan {
  name: string;
  description: string;
  price: number;
  users: number;
  limits: PlanLimits;
  features: string[];
  recommended?: boolean;
}

@Component({
    selector: 'plans-modal',
    templateUrl: './plans-modal.component.html',
    styleUrls: ['./plans-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PlansModalComponent extends AbstractComponent implements OnInit {

  billingCycle: BillingCycle = 'monthly';

  plans: Plan[] = [
    {
      name: 'Trend Start',
      description: 'plans.data.start.description',
      price: 297,
      users: 3,
      limits: {
        campaigns: '1.000',
        integrations: '1.000',
        contacts: '1.000'
      },
      features: [
        'plans.data.feature.whatsappOfficial',
        'plans.data.feature.whatsappTrendBusiness',
        'plans.data.feature.instagramMessenger',
        'plans.data.feature.trackingModule',
        'plans.data.feature.customerPortfolio',
        'plans.data.feature.tasksModule',
        'plans.data.feature.additionalTrendBusiness',
        'plans.data.feature.volumeStart',
      ]
    },
    {
      name: 'Trend Pro',
      description: 'plans.data.pro.description',
      price: 497,
      users: 10,
      recommended: true,
      limits: {
        campaigns: '10.000',
        integrations: '10.000',
        contacts: '10.000'
      },
      features: [
        'plans.data.feature.whatsappOfficial',
        'plans.data.feature.whatsappTrendBusiness',
        'plans.data.feature.instagramMessenger',
        'plans.data.feature.trackingModule',
        'plans.data.feature.customerPortfolio',
        'plans.data.feature.tasksModule',
        'plans.data.feature.additionalTrendBusiness',
        'plans.data.feature.volumePro',
      ]
    },
    {
      name: 'Trend Expert',
      description: 'plans.data.expert.description',
      price: 697,
      users: 20,
      limits: {
        campaigns: '50.000',
        integrations: '50.000',
        contacts: '50.000'
      },
      features: [
        'plans.data.feature.whatsappOfficial',
        'plans.data.feature.whatsappTrendBusiness',
        'plans.data.feature.instagramMessenger',
        'plans.data.feature.trackingModule',
        'plans.data.feature.customerPortfolio',
        'plans.data.feature.tasksModule',
        'plans.data.feature.additionalTrendBusiness',
        'plans.data.feature.volumeExpert',
      ]
    }
  ];

  envPlans: object = {};

  constructor(
    injector: Injector,
    private dialogRef: MatDialogRef<PlansModalComponent>,
    private paymentService: PaymentService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.loading = false;
    this.getAsaasPlans();
  }

  getStripePlansEnv() {
    this.paymentService.getStripePlans(environment.env)
      .pipe(takeUntil(this.destroy$))
      .subscribe((plans: object) => {
        this.envPlans = plans;
      });
  }

  getAsaasPlans() {
    this.paymentService.getAsaasPlans(environment.env)
      .pipe(takeUntil(this.destroy$))
      .subscribe((plans: object) => {
        this.envPlans = plans;
      });
  }

  setBillingCycle(cycle: BillingCycle): void {
    this.billingCycle = cycle;
  }

  getPrice(plan: Plan): number {
    return plan.price;
  }

  getFullTotal(plan: Plan): number {
    switch (this.billingCycle) {
      case 'yearly':
        return plan.price * 12;
      case 'semiannual':
        return plan.price * 6;
      default:
        return plan.price;
    }
  }

  getDiscountedTotal(plan: Plan): number {
    const total = this.getFullTotal(plan);
    switch (this.billingCycle) {
      case 'yearly':
        return total * 0.85;
      case 'semiannual':
        return total * 0.9;
      default:
        return total;
    }
  }

  getDiscountPercent(): number {
    if (this.billingCycle === 'yearly') {
      return 15;
    }
    if (this.billingCycle === 'semiannual') {
      return 10;
    }
    return 0;
  }

  getPeriodLabel(): string {
    switch (this.billingCycle) {
      case 'yearly':
        return this.t('plans.modal.periodYearly');
      case 'semiannual':
        return this.t('plans.modal.periodSemiannual');
      default:
        return this.t('plans.modal.periodPerMonth');
    }
  }

  getBillingLabel(): string {
    switch (this.billingCycle) {
      case 'yearly':
        return this.t('plans.modal.billingYearly');
      case 'semiannual':
        return this.t('plans.modal.billingSemiannual');
      default:
        return this.t('plans.modal.billingMonthly');
    }
  }

  public async onPurchase(plan: Plan): Promise<void> {
    this.loading = true;
    const priceIds = {
      'Trend Start': this.envPlans[this.billingCycle].start,
      'Trend Pro': this.envPlans[this.billingCycle].pro,
      'Trend Expert': this.envPlans[this.billingCycle].expert,
    };
    this.paymentService.asaasCreateCheckoutSession(priceIds[plan.name])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (checkoutUrl: string) => {
          window.open(checkoutUrl, '_blank');
        },
        complete: () => this.loading = false
      });
  }

  onContinueFree(): void {
    this.dialogRef.close();
  }

  close(): void {
    this.dialogRef.close();
  }
}
